/**
 * Runtime Validation Utilities
 * Provides safe validation and sanitization for API responses
 */

import { z } from 'zod';
import DOMPurify from 'dompurify';

/**
 * Validation error class for better error handling
 */
export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly errors: z.ZodError | null = null,
    public readonly data: unknown = null
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Validates data against a Zod schema
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @param context - Optional context for error messages
 * @returns Validated and typed data
 * @throws ValidationError if validation fails
 */
export function validate<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  context?: string
): z.infer<T> {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessage = context
        ? `Validation failed for ${context}: ${error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`
        : `Validation failed: ${error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`;
      
      console.error('Validation Error:', {
        context,
        errors: error.errors,
        data: JSON.stringify(data, null, 2).substring(0, 500) // Log first 500 chars
      });
      
      throw new ValidationError(errorMessage, error, data);
    }
    throw error;
  }
}

/**
 * Safely validates data, returning null on failure instead of throwing
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @returns Validated data or null if validation fails
 */
export function safeValidate<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown
): z.infer<T> | null {
  const result = schema.safeParse(data);
  if (result.success) {
    return result.data;
  }
  console.warn('Safe validation failed:', result.error.errors);
  return null;
}

/**
 * Validates an array of items
 * @param schema - Zod schema for individual items
 * @param data - Array to validate
 * @param context - Optional context for error messages
 * @returns Validated array
 */
export function validateArray<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  context?: string
): Array<z.infer<T>> {
  if (!Array.isArray(data)) {
    throw new ValidationError(
      `Expected array${context ? ` for ${context}` : ''}, got ${typeof data}`
    );
  }
  
  return data.map((item, index) => {
    try {
      return validate(schema, item, context ? `${context}[${index}]` : `item[${index}]`);
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }
      throw new ValidationError(
        `Validation failed for item ${index}${context ? ` in ${context}` : ''}`,
        null,
        item
      );
    }
  });
}

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param html - HTML string to sanitize
 * @param config - Optional DOMPurify configuration
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(
  html: string,
  config?: DOMPurify.Config
): string {
  const defaultConfig: DOMPurify.Config = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'code', 'pre'],
    ALLOWED_ATTR: ['href', 'title', 'target'],
    ALLOW_DATA_ATTR: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    ...config
  };
  
  return DOMPurify.sanitize(html, defaultConfig) as string;
}

/**
 * Sanitizes user input by removing potentially dangerous characters
 * @param input - Input string to sanitize
 * @returns Sanitized string
 */
export function sanitizeInput(input: string): string {
  // Remove null bytes, control characters, and normalize whitespace
  return input
    .replace(/\0/g, '') // Remove null bytes
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '') // Remove control characters
    .trim();
}

/**
 * Sanitizes an object by sanitizing all string values
 * @param obj - Object to sanitize
 * @returns Sanitized object
 */
export function sanitizeObject<T extends Record<string, unknown>>(obj: T): T {
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeObject(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item =>
        typeof item === 'string' ? sanitizeInput(item) :
        item !== null && typeof item === 'object' ? sanitizeObject(item as Record<string, unknown>) :
        item
      );
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized as T;
}

/**
 * Validates and sanitizes API response data
 * @param schema - Zod schema to validate against
 * @param data - Response data to validate
 * @param context - Optional context for error messages
 * @returns Validated and sanitized data
 */
export function validateAndSanitize<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  context?: string
): z.infer<T> {
  // First validate the structure
  const validated = validate(schema, data, context);
  
  // Then sanitize string values if it's an object
  if (validated !== null && typeof validated === 'object') {
    return sanitizeObject(validated as Record<string, unknown>) as z.infer<T>;
  }
  
  return validated;
}

/**
 * Creates a validated API response wrapper
 * @param schema - Schema for the response data
 * @param data - Response data
 * @param context - Optional context
 * @returns Validated response
 */
export function createValidatedResponse<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  context?: string
): { success: true; data: z.infer<T> } | { success: false; error: string } {
  try {
    const validated = validateAndSanitize(schema, data, context);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof ValidationError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: 'Unknown validation error' };
  }
}

/**
 * Type guard to check if a value is a validation error
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Extracts error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ValidationError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unknown error occurred';
}
